import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AnalyticsOverview {
  total_schools: number;
  active_schools: number;
  suspended_schools: number;
  new_schools_this_month: number;
  total_users: number;
  new_users_this_month: number;
  total_assessments: number;
  assessments_this_month: number;
  avg_assessments_per_active_school: number | null;
  active_schools_this_month: number;
}

export interface PlanDistRow {
  plan_tier: string;
  plan_sub_tier: string;
  billing_status: string;
  cnt: number;
}

export interface BillingStatusRow {
  billing_status: string;
  cnt: number;
}

export interface UsageBandRow {
  band: string;
  cnt: number;
}

export interface TopSchoolRow {
  school_id: string;
  school_name: string;
  plan_tier: string | null;
  plan_sub_tier: string | null;
  total_assessments: number;
}

export interface UnderusedSchoolRow {
  school_id: string;
  school_name: string;
  plan_tier: string | null;
  plan_sub_tier: string | null;
  billing_status: string | null;
  last_assessment_at: string | null;
}

export interface AdoptionStats {
  schools_with_pupils: number;
  schools_with_classes: number;
  schools_with_completed_assessment: number;
  monthly_active_schools: number;
}

export interface InviteStats {
  total_invites: number;
  accepted_invites: number;
}

export interface GradeDistRow {
  grade: string;
  cnt: number;
}

export interface StatusDistRow {
  status: string;
  cnt: number;
}

export interface FluencyAverages {
  avg_syntactic_maturity: number | null;
  avg_textual_cohesion: number | null;
  avg_lexical_precision: number | null;
  fluency_sample_count: number;
}

export interface CapProximityRow {
  school_id: string;
  school_name: string;
  plan_tier: string | null;
  plan_sub_tier: string | null;
  billing_status: string | null;
  assessments_this_month: number;
  monthly_cap: number;
}

export interface AllSchoolRow {
  school_id: string;
  school_name: string;
  plan_tier: string | null;
  plan_sub_tier: string | null;
  billing_status: string | null;
  suspended_at: string | null;
  school_created_at: string | null;
  total_assessments: number;
  assessments_this_month: number;
  assessments_last_30d: number;
  last_assessment_at: string | null;
  // filter-only fields — not rendered in tables, not included in CSV exports
  admin_email: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export interface RangeStats {
  new_schools: number;
  new_users: number;
  assessments: number;
  active_schools: number;
}

export interface PlatformAnalytics {
  overview: AnalyticsOverview;
  plan_distribution: PlanDistRow[];
  billing_status_counts: BillingStatusRow[];
  usage_bands: UsageBandRow[];
  top_schools: TopSchoolRow[];
  underused_schools: UnderusedSchoolRow[];
  adoption: AdoptionStats;
  invite_stats: InviteStats;
  grade_distribution: GradeDistRow[];
  status_distribution: StatusDistRow[];
  fluency_averages: FluencyAverages;
  cap_proximity: CapProximityRow[];
  all_schools: AllSchoolRow[];
}

export interface TrendRow {
  month_label: string;
  assessment_count: number;
  active_school_count: number;
}

export function useAdminAnalytics() {
  return useQuery({
    queryKey: ['admin-analytics'],
    queryFn: async (): Promise<PlatformAnalytics> => {
      const { data, error } = await supabase.rpc('get_platform_analytics');
      if (error) throw error;
      return data as PlatformAnalytics;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminRangeStats(from: string | null, to: string | null) {
  return useQuery({
    queryKey: ['admin-analytics-range', from, to],
    queryFn: async (): Promise<RangeStats> => {
      const { data, error } = await supabase.rpc('get_platform_range_stats', {
        p_from: from + 'T00:00:00.000Z',
        p_to:   to   + 'T23:59:59.999Z',
      });
      if (error) throw error;
      return data as RangeStats;
    },
    enabled: !!from && !!to,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAdminAssessmentTrend() {
  return useQuery({
    queryKey: ['admin-analytics-trend'],
    queryFn: async (): Promise<TrendRow[]> => {
      const { data, error } = await supabase.rpc('get_platform_assessment_trend');
      if (error) throw error;
      return (data ?? []) as TrendRow[];
    },
    staleTime: 5 * 60 * 1000,
  });
}
